import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import axios from "@api/axios";
import { CircularProgress } from "@material-ui/core";
import { AdyenCheckout, Card } from '@adyen/adyen-web';
import '@adyen/adyen-web/styles/adyen.css';
import config from '../../../config';
import { baseStyles, supportedBrands } from './AdyenConstants';
import styles from './AdyenPayment.module.scss';

const AdyenPayment = ({accountId, adyenCardRef, amount, email, handleAdyenChange, chargeAdyenPayment}) => {
	const [isAdyenReady, setIsAdyenReady] = useState(false);
	const [session, setSession] = useState(null);
	const checkoutRef = useRef(null);
	const account = useSelector((state) => state.account);

	useEffect(() => {
		getSession();
	}, []);

	const getSession = async () => {
		const res = await axios.post(`${config.WORKIZ_BOOKING_API}/getWorkizPaySession/`, {
			accountId,
			amount: amount.toFixed(2),
			clientEmail: email,
			saveCard: false,
		});
		if (res.data.flag) {
			setSession(res.data.data);
		} else {
			throw new Error('failed to get session');
		}
	};

	const globalConfiguration = {
		environment: process.env.REACT_APP_STAGE === 'prod' ? 'live' : 'test',
		showPayButton: false,
		clientKey: config.ADYEN_CLIENT_KEY,
		locale: 'en-US',
		countryCode: account.region === 'NON' ? 'US' : account.region,
		analytics: {
		  enabled: false,
		},
		session: {
		  id: session?.id,
		  sessionData: session?.sessionData,
		  shopperEmail: session?.shopperEmail,
		},
	};

	const handleAdyensSubmit = async (state, component, actions) => {
		try {
			const response = await chargeAdyenPayment(state);
			if (!response.flag) {
				actions.reject();
			} else {
				actions.resolve(response.data);
			}
			return response;
		} catch (error) {
			console.log('Error during payment submission:', error);
			actions.reject();
		}
	};
	
	const handleInputChange = (state) => {
		handleAdyenChange(state.isValid);
	};

	const cardConfig = useMemo(
		() => ({
			placeholders: {
				cardNumber: 'As shown on card',
				expiryDate: 'MM/YY',
				securityCodeThreeDigits: 'CVV',
			},
			type: 'card',
			brands: supportedBrands,
			showBrandIcon: false,
			billingAddressMode: 'full',
			exposeExpiryDate: true,
			onChange: handleInputChange,
			onError: (error) => {
				console.error('Adyen error:', error);
			},
			onSubmit: handleAdyensSubmit,
			onConfigSuccess: () => {
				setIsAdyenReady(true);
			},
			styles: baseStyles
	}), []);

	useEffect(() => {
		const initializeCheckout = async () => {
			if (!checkoutRef.current || !session) {
				return;
			}
			try {
				const checkout = await AdyenCheckout(globalConfiguration);
				const cardDiv = document.createElement('div');
				checkoutRef.current.appendChild(cardDiv);
				const cardComponent = new Card(checkout, cardConfig);
				if (adyenCardRef) {
					adyenCardRef.current = cardComponent;
					adyenCardRef.current.mount(cardDiv);
				}
			} catch (err) {
				console.error('Failed to initialize Adyen:', err);
			}
		};
		initializeCheckout();
	
		return () => {
			if (checkoutRef.current) {
				checkoutRef.current.innerHTML = '';
			}
		};
	}, [session, cardConfig]);

    return (
        <>
            {!isAdyenReady && <div className={`main-loader ${styles.loader}`}><CircularProgress size={40}/></div>}
            <div className={!isAdyenReady ? styles.hide : null}>
                <div ref={checkoutRef} />
            </div>
        </>
    );
}
export default AdyenPayment;